<template>
  <KeepAlive>
    <Transition mode="out-in" name="v-fade-fast">
      <div :key="mode" :class="`v-calendar-${mode}`" @mouseleave="hoverIndex = undefined">
        <div v-if="mode === 'days'" class="v-calendar__row v-calendar__row-header">
          <div v-for="(header, index) in headers" :key="index" class="v-calendar__item">
            <div class="v-calendar-spacer" />
            <div style="z-index: 1" v-text="header" />
            <div class="v-calendar-spacer" />
          </div>
        </div>
        <div v-for="(row, rowIndex) in rows" :key="rowIndex" class="v-calendar__row">
          <VCalendarItem
            v-for="(item, colIndex) in row"
            :key="colIndex"
            :is-first-active="isFirstActive(item, colIndex)"
            :is-first-selected="isFirstSelected(item, colIndex)"
            :is-last-active="isLastActive(item, colIndex)"
            :is-last-selected="isLastSelected(item, colIndex)"
            :item
            @click="onClickItem(item)"
            @mouseover="onMouseOver(item)"
          />
        </div>
      </div>
    </Transition>
  </KeepAlive>
</template>

<script setup lang="ts">
import { chunk } from 'lodash-es'
import { computed, ref } from 'vue'

import { type Dayjs, dayjs } from '../../utils/day.ts'
import { type ModelValue } from './VCalendar.vue'
import VCalendarItem from './VCalendarItem.vue'

export interface Date {
  date: Dayjs
  text: string
  fade: boolean
  disabled: boolean
}

export interface Item extends Date {
  index: number
  selected: boolean
  active: boolean
}

export type Mode = 'days' | 'months' | 'years'

interface Props {
  date: Dayjs
  mode: Mode
  isRange?: boolean
  dateIsDisabled?: (date: Dayjs) => boolean
  min?: Dayjs | null
  max?: Dayjs | null
}

const props = withDefaults(defineProps<Props>(), {
  isRange: false,
  dateIsDisabled: () => false,
  min: null,
  max: null,
})

const model = defineModel<ModelValue>({ default: null })

const hoverIndex = ref<number>()

const headers = Array.from(dayjs.weekdaysShort())
const first = headers.shift()
if (first) {
  headers.push(first)
}

const cols = computed(() => ({ days: headers.length, months: 3, years: 4 })[props.mode])
const rows = computed(() => chunk(items.value, cols.value))
const dates = computed(() => ({ days, years, months })[props.mode].value)

const years = computed((): Date[] => {
  const years = []

  let date = props.date.subtract(9, 'years')

  while (date.year() <= props.date.add(14, 'years').year()) {
    years.push({ date, text: date.format('YYYY'), fade: false, disabled: isDisabled(date, 'year') })
    date = date.add(1, 'year')
  }

  return years
})

const months = computed((): Date[] => {
  const months = []

  // Get the first month of the year.
  let date = props.date.startOf('year').startOf('month')

  while (date.month() <= 11 && date.year() === props.date.year()) {
    months.push({ date, text: date.format('MMMM'), fade: false, disabled: isDisabled(date, 'month') })
    date = date.add(1, 'month')
  }

  return months
})

const days = computed((): Date[] => {
  const days = []

  // Get the first monday at the start of the month.
  let date = props.date.startOf('month').startOf('week').day(1)

  // Fill up the days before the current month.
  while (date.month() !== props.date.month()) {
    days.push({ date, text: String(date.date()), fade: true, disabled: isDisabled(date) })
    date = date.add(1, 'day')
  }

  while (date.date() <= props.date.daysInMonth() && date.month() === props.date.month()) {
    days.push({ date, text: String(date.date()), fade: false, disabled: isDisabled(date) })
    date = date.add(1, 'day')
  }

  // Fill up the remainder of days for the week.
  while (days.length % headers.length !== 0) {
    days.push({ date, text: String(date.date()), fade: true, disabled: isDisabled(date) })
    date = date.add(1, 'day')
  }

  return days
})

const items = computed((): Item[] => {
  return dates.value.map((date, index) => {
    const selected = isSelected(date.date)

    return {
      index,
      selected,
      active: selected || isActive(index),
      ...date,
    }
  })
})

const firstActiveIndex = computed(() => {
  return items.value.findIndex((row) => row.active)
})

const lastActiveIndex = computed(() => {
  return items.value.reduce((acc, row, index) => {
    if (row.active) {
      acc = index
    }

    return acc
  }, 0)
})

const firstSelectedIndex = computed(() => dates.value.findIndex((row) => isSelected(row.date)))

const lastSelectedIndex = computed(() => {
  return items.value.reduce((acc, row, index) => {
    if (row.selected) {
      acc = index
    }

    return acc
  }, 0)
})

function isDisabled(date: Dayjs, unit?: dayjs.QUnitType) {
  if (props.min && date.isBefore(props.min, unit)) {
    return true
  }

  if (props.max && date.isAfter(props.max, unit)) {
    return true
  }

  return props.dateIsDisabled(date)
}

function onMouseOver(item: Item) {
  if (Array.isArray(model.value) && model.value[0] && !model.value[1]) {
    hoverIndex.value = item.index
  }
}

function isActive(index: number) {
  if (!hoverIndex.value) {
    return false
  }

  if (index === hoverIndex.value) {
    return true
  }

  if (!Array.isArray(model.value)) {
    return false
  }

  if (model.value[0] === null) {
    return false
  }

  if (firstSelectedIndex.value === -1) {
    return false
  }

  if (hoverIndex.value > firstSelectedIndex.value && index > firstSelectedIndex.value && index <= hoverIndex.value) {
    return true
  }
  if (hoverIndex.value < firstSelectedIndex.value && index < firstSelectedIndex.value && index >= hoverIndex.value) {
    return true
  }

  return false
}

function isSelected(date: Dayjs) {
  if (Array.isArray(model.value)) {
    if (model.value[0] && model.value[1]) {
      return date.isSameOrAfter(model.value[0], props.mode) && date.isSameOrBefore(model.value[1], 'day')
    }

    return date.isSame(model.value[0], props.mode)
  }

  return date.isSame(model.value, props.mode)
}

function onClickItem(item: Item) {
  if (item.disabled) {
    return
  }

  if (Array.isArray(model.value)) {
    if (model.value[0] !== null && model.value[1] === null) {
      if (item.date.isBefore(model.value[0])) {
        model.value = [item.date, model.value[0]]
      } else {
        model.value = [model.value[0], item.date]
      }
    } else {
      model.value = [item.date, null]
    }
  } else {
    model.value = item.date
  }

  hoverIndex.value = undefined
}

function isFirstActive(item: Item, columnIndex: number) {
  return item.active && (columnIndex === 0 || item.index === firstActiveIndex.value)
}

function isLastActive(item: Item, columnIndex: number) {
  return item.active && (columnIndex === cols.value - 1 || item.index === lastActiveIndex.value)
}

function isFirstSelected(item: Item, columnIndex: number) {
  return item.selected && (columnIndex === 0 || item.index === firstSelectedIndex.value)
}

function isLastSelected(item: Item, columnIndex: number) {
  return item.selected && (columnIndex === cols.value - 1 || item.index === lastSelectedIndex.value)
}
</script>

<style>
.v-calendar__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.v-calendar__row-header {
  font-weight: 600;
  text-transform: uppercase;

  > div {
    width: 24px;
  }
}

.v-calendar-months .v-calendar__row {
  margin-bottom: 15px;
}
</style>
